import { Injectable } from '@angular/core';
import { toGroupchatType } from '@shared/ProjectKeyValStore';
import { BotService } from './bot.service';
import { ProjectKeyValService } from './project-key-val.service';

/**
 * Ermöglicht Bots, über einen gemeinsamen Kanal Nachrichten auszutauschen (`RAGAI.GROUPCHAT`).
 * @remarks
 * Reine Convention-Schicht über `RAGAI.PROJECTKEYVAL`: jeder Kanal ist intern nur ein eigener KeyVal-Typ
 * (über `toGroupchatType(channel)`), jede Nachricht ein per {@link ProjectKeyValService.unique} zeitgestempelter
 * Eintrag mit Schlüssel `"msg"`. Da `RAGAI.PROJECTKEYVAL` projektweit (nicht nur bot-spezifisch) speichert,
 * können verschiedene Bots desselben Projekts über denselben Kanalnamen tatsächlich miteinander kommunizieren.
 */
@Injectable({
  providedIn: 'root'
})
export class BotGroupchatService {

  constructor(
    private projectKeyVal: ProjectKeyValService,
    private botService: BotService
  ) { }

  private getBotName(): string {
    return this.botService.botConfig?.name ?? 'Unknown Bot';
  }

  /**
   * Sendet eine Nachricht in den angegebenen Gruppenchat-Kanal.
   * @param message Der Nachrichtentext.
   * @param channel Der Ziel-Kanal.
   * @returns Nichts.
   */
  public async send(message: string, channel: string): Promise<void> {
    const value = this.getBotName() + ': ' + message;
    await this.projectKeyVal.unique('msg', value, toGroupchatType(channel));
  }

  /**
   * Liest alle Nachrichten eines Gruppenchat-Kanals.
   * @param channel Der Kanal, dessen Nachrichten gelesen werden sollen.
   * @returns Die Nachrichten, durch Leerzeilen getrennt.
   */
  public async read(channel: string): Promise<string> {
    const entries = await this.projectKeyVal.getByType(toGroupchatType(channel));
    return entries.map(e => e.value).join('\n\n');
  }

  /**
   * Löscht alle Nachrichten eines Gruppenchat-Kanals.
   * @param channel Der zu leerende Kanal.
   * @returns Nichts.
   */
  public async delete(channel: string): Promise<void> {
    const entries = await this.projectKeyVal.getByType(toGroupchatType(channel));
    for (const entry of entries) {
      await this.projectKeyVal.deleteKeyValEntry(entry.key, entry.type);
    }
  }
}
